import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { ErrorCategory } from '../../types';

interface ErrorAnalysisProps {
  errors: ErrorCategory[];
  title?: string;
}

const ERROR_COLORS: Record<string, string> = {
  'Timeout': '#A82323',
  'Rate Limited': '#6D9E51',
  'Invalid Scan': '#BCD9A2',
  'AI Generation Error': '#A82323',
  'API Error': '#A82323',
  'Other': '#BCD9A2',
  'Unknown': '#FEFFD3',
};

function getErrorColor(category: string): string {
  return ERROR_COLORS[category] || ERROR_COLORS['Other'];
}

export function ErrorAnalysis({ errors, title = 'Error Analysis' }: ErrorAnalysisProps) {
  const hasData = errors.length > 0;
  const totalErrors = errors.reduce((sum, e) => sum + e.count, 0);

  const chartData = errors.map(e => ({
    name: e.category,
    value: e.count,
    percent: e.percent,
    color: getErrorColor(e.category),
  }));

  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <div className="w-7 h-7 rounded bg-red-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
        {hasData && (
          <div className="ml-auto text-center">
            <p className="text-lg font-bold text-danger-dark">{totalErrors}</p>
            <p className="text-[10px] text-surface-500 uppercase">Errors</p>
          </div>
        )}
      </div>

      {hasData ? (
        <>
          {/* Pie Chart */}
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="30%"
                  outerRadius="60%"
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  formatter={(value: number, name: string) => [`${value} errors`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Horizontal legend */}
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 pt-2 border-t border-surface-100 flex-shrink-0">
            {errors.map((error) => (
              <div key={error.category} className="flex items-center gap-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: getErrorColor(error.category) }}
                />
                <span className="text-[10px] text-surface-600">{error.category}</span>
                <span className="text-[10px] font-medium text-surface-700">{error.count}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center text-surface-400">
          <div className="text-center">
            <svg className="w-10 h-10 mx-auto mb-1 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-success-dark">No errors in this period!</p>
          </div>
        </div>
      )}
    </div>
  );
}
